import { AppIcon } from '../types/apps';
// import { createFileLogger } from '../utils/logger';
// const log = createFileLogger('ENTITYCREATIONREGISTRY_SERVICE', { enabled: false });

/**
 * Metadata for entity creation commands
 * Apps register their entity types here to appear in EntitySidebar's "New" menu
 */
export interface EntityCreationMetadata {
  /** Command ID to execute when creating this entity type */
  commandId: string;
  
  /** Entity type this creates (e.g., 'Doc', 'Note', 'Recipe') */
  entityType: string;
  
  /** Display name in UI (e.g., "Document", "Note") */
  displayName: string;
  
  /** Icon definition */
  icon: AppIcon;
  
  /** Short description shown in tooltips/menus */
  description?: string;
  
  /** Can this entity be created inside folders? */
  canCreateInFolder: boolean;
  
  /** Should we open a window after creating? */
  openAfterCreate: boolean;
  
  /** Category for grouping in menus */
  category?: 'document' | 'note' | 'media' | 'data' | 'other' | 'productivity' | 'system';
  
  /** Sort order (lower = earlier in menu) */
  sortOrder?: number;
  
  /** Keyboard shortcut hint (e.g., "⌘N") */
  shortcut?: string;

  /**
   * Hide this "New X" entry from discovery surfaces (EntitySidebar's "New"
   * menu, GlobalEntitySearch's Actions list) while leaving the command and
   * entity type fully functional for direct/AI-driven creation. Mirrors the
   * `hidden` flag on the owning app's own definition.ts.
   */
  hidden?: boolean;
}

/**
 * Registry for entity creation commands
 * 
 * Apps register their entity types here so EntitySidebar can discover them
 * without hardcoding entity schemas.
 * 
 * Usage:
 * ```typescript
 * // In your app's definition.ts or commands/index.ts
 * entityCreationRegistry.register({
 *   commandId: 'notes_create',
 *   entityType: 'Note',
 *   displayName: 'Note',
 *   icon: { type: 'phosphor', phosphorIcon: 'Note', phosphorWeight: 'duotone' },
 *   canCreateInFolder: true,
 *   openAfterCreate: true,
 *   category: 'note',
 *   sortOrder: 2
 * });
 * ```
 */
class EntityCreationRegistry {
  private creators = new Map<string, EntityCreationMetadata>();
  
  /**
   * Register an entity creation command
   */
  register(metadata: EntityCreationMetadata): void {
    if (this.creators.has(metadata.commandId)) {
      console.warn(`[EntityCreationRegistry] Overwriting existing creator: ${metadata.commandId}`);
    }
    
    this.creators.set(metadata.commandId, metadata);
    // console.log(`[EntityCreationRegistry] Registered creator: ${metadata.displayName} (${metadata.commandId})`);
  }
  
  /**
   * Unregister an entity creation command
   */
  unregister(commandId: string): void {
    this.creators.delete(commandId);
  }
  
  /**
   * Get all registered entity creators that are not marked `hidden`.
   */
  getAll(): EntityCreationMetadata[] {
    return Array.from(this.creators.values()).filter(c => !c.hidden).sort((a, b) => {
      // Sort by sortOrder first, then by displayName
      const orderA = a.sortOrder ?? 999;
      const orderB = b.sortOrder ?? 999;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.displayName.localeCompare(b.displayName);
    });
  }
  
  /**
   * Get entity creators with filtering
   */
  getCreators(options?: { 
    canCreateInFolder?: boolean;
    category?: string;
    entityType?: string;
  }): EntityCreationMetadata[] {
    let creators = this.getAll();
    
    if (options?.canCreateInFolder !== undefined) {
      creators = creators.filter(c => c.canCreateInFolder === options.canCreateInFolder);
    }
    
    if (options?.category) {
      creators = creators.filter(c => c.category === options.category);
    }
    
    if (options?.entityType) {
      creators = creators.filter(c => c.entityType === options.entityType);
    }
    
    return creators;
  }
  
  /**
   * Get a specific creator by command ID
   */
  get(commandId: string): EntityCreationMetadata | undefined {
    return this.creators.get(commandId);
  }
  
  /**
   * Get a creator by entity type
   */
  getByEntityType(entityType: string): EntityCreationMetadata | undefined {
    return Array.from(this.creators.values()).find(c => c.entityType === entityType);
  }
  
  /**
   * Check if an entity type has a registered creator
   */
  hasCreator(entityType: string): boolean {
    return this.getByEntityType(entityType) !== undefined;
  }
}

export const entityCreationRegistry = new EntityCreationRegistry();

